import { createClient } from '@/lib/supabase/client';
import type {
  RIIPFormData,
  MerchFormData,
  HostFormData,
  BookExperienceFormData,
  PreBookFormData,
  TourOperatorInterestData,
} from '@/lib/types/forms';
import { nicheLabels } from '@/lib/tour-operator-interest';

export type PreBookInquiryContext = {
  experienceName: string;
  experienceSlug?: string;
  location?: string;
  sourcePath?: string;
};

type LeadDetail = { label: string; value: string };

// Fire-and-forget admin alert: the Supabase insert above already succeeded,
// so an email failure must never fail the visitor's submission.
function notifyAdmin(type: string, title: string, details: LeadDetail[]) {
  try {
    void fetch('/api/lead-notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, title, details }),
    }).catch(() => {});
  } catch {
    // ignore — notifications must not break form UX
  }
}

export async function submitRIIPForm(data: RIIPFormData, programmeType: 'riip' | 'cap' = 'riip') {
  const supabase = await createClient();

  const { error } = await supabase.from('riip_inquiries').insert({
    programme_type: programmeType,
    name: data.name,
    designation: data.designation,
    institution_type: data.institutionType,
    institution_name: data.institutionName,
    email: data.email ?? null,
    phone: data.phone ?? null,
    group_size: data.groupSize ? parseInt(data.groupSize, 10) : null,
  });

  if (error) throw new Error('Failed to submit form');

  notifyAdmin(programmeType, data.name, [
    { label: 'Programme', value: programmeType.toUpperCase() },
    { label: 'Name', value: data.name },
    { label: 'Designation', value: data.designation },
    { label: 'Institution type', value: data.institutionType },
    { label: 'Institution', value: data.institutionName },
    { label: 'Email', value: data.email?.trim() || 'Not provided' },
    { label: 'Phone', value: data.phone?.trim() || 'Not provided' },
    { label: 'Group size', value: data.groupSize?.trim() || 'Not provided' },
  ]);
}

export async function submitMerchForm(data: MerchFormData) {
  const supabase = await createClient();

  const { error } = await supabase.from('merch_inquiries').insert({
    name: data.name,
    quantity: Number(data.quantity),
    email: data.email ?? null,
    phone: data.phone ?? null,
    event_type: data.eventType,
    message: data.message ?? null,
    attachments: null,
  });

  if (error) throw new Error('Failed to submit form');

  notifyAdmin('merch', data.name, [
    { label: 'Name', value: data.name },
    { label: 'Email', value: data.email?.trim() || 'Not provided' },
    { label: 'Phone', value: data.phone?.trim() || 'Not provided' },
    { label: 'Event type', value: data.eventType },
    { label: 'Quantity', value: data.quantity },
    { label: 'Message', value: data.message?.trim() || 'Not provided' },
  ]);
}

export async function submitHostForm(data: HostFormData) {
  const supabase = await createClient();

  const { error } = await supabase.from('host_applications').insert({
    name: data.name,
    email: data.email ?? null,
    phone: data.phone ?? null,
    district: data.district,
    category: data.category,
  });

  if (error) throw new Error('Failed to submit form');

  notifyAdmin('host', data.name, [
    { label: 'Name', value: data.name },
    { label: 'Email', value: data.email?.trim() || 'Not provided' },
    { label: 'Phone', value: data.phone?.trim() || 'Not provided' },
    { label: 'District', value: data.district },
    { label: 'Category', value: data.category },
  ]);
}

export async function submitBookExperienceForm(data: BookExperienceFormData) {
  const supabase = await createClient();

  const { error } = await supabase.from('experience_bookings').insert({
    name: data.name,
    email: data.email ?? null,
    phone: data.phone ?? null,
    district: data.district,
    preferred_dates: data.preferredDates?.toISOString().split('T')[0] ?? null,
    dates_not_decided: data.datesNotDecided,
    group_size: parseInt(data.groupSize, 10),
    group_type: data.groupType!,
  });

  if (error) throw new Error('Failed to submit form');

  notifyAdmin('book_experience', data.name, [
    { label: 'Name', value: data.name },
    { label: 'Email', value: data.email?.trim() || 'Not provided' },
    { label: 'Phone', value: data.phone?.trim() || 'Not provided' },
    { label: 'District', value: data.district },
    { label: 'Preferred date', value: data.datesNotDecided ? 'Not decided' : (data.preferredDates?.toISOString().split('T')[0] ?? 'Not provided') },
    { label: 'Group size', value: data.groupSize },
    { label: 'Group type', value: data.groupType ?? 'Not provided' },
  ]);
}
export async function submitPreBookForm(data: PreBookFormData, context: PreBookInquiryContext) {
  const supabase = await createClient();

  const { error } = await supabase.from('pre_book_inquiries').insert({
    name: data.name,
    email: data.email?.trim() || null,
    phone: data.phone?.trim() || null,
    experience_name: context.experienceName,
    experience_slug: context.experienceSlug ?? null,
    location: context.location ?? null,
    session_type: data.sessionType,
    preferred_date: data.preferredDate.toISOString().split('T')[0],
    preferred_session: data.preferredSession,
    total_persons: parseInt(data.totalPersons, 10),
    source_path: context.sourcePath ?? null,
  });

  if (error) throw new Error('Failed to submit form');

  notifyAdmin('pre_book', data.name, [
    { label: 'Name', value: data.name },
    { label: 'Email', value: data.email?.trim() || 'Not provided' },
    { label: 'Phone', value: data.phone?.trim() || 'Not provided' },
    { label: 'Experience', value: context.experienceName },
    { label: 'Location', value: context.location || 'Not provided' },
    { label: 'Session type', value: data.sessionType },
    { label: 'Preferred date', value: data.preferredDate.toISOString().split('T')[0] },
    { label: 'Preferred session', value: data.preferredSession },
    { label: 'Total persons', value: data.totalPersons },
    { label: 'Source page', value: context.sourcePath || 'Not provided' },
  ]);
}

// Fire-and-forget: emails the partner catalogue (PDF attached). The route
// re-reads the registration server-side, so only the email is sent.
function requestPartnerCatalogueEmail(email: string) {
  try {
    void fetch('/api/partner-catalogue', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }).catch(() => {});
  } catch {
    // ignore — the catalogue is also downloadable from the success screen
  }
}

export type TourOperatorInterestContext = {
  /** Campaign tag from the QR / link (?src=…). */
  source?: string;
  sourcePath?: string;
};

export async function submitTourOperatorInterest(
  data: TourOperatorInterestData,
  context: TourOperatorInterestContext,
) {
  const supabase = await createClient();
  const nicheOther = data.niches.includes('other') ? data.nicheOther?.trim() || null : null;

  const { error } = await supabase.from('tour_operator_interests').insert({
    name: data.name.trim(),
    company: data.company.trim(),
    phone: data.phone.trim(),
    email: data.email?.trim() || null,
    niches: data.niches,
    niche_other: nicheOther,
    source: context.source ?? null,
    source_path: context.sourcePath ?? null,
  });

  if (error) throw new Error('Failed to submit form');

  const email = data.email?.trim();
  if (email) requestPartnerCatalogueEmail(email);

  notifyAdmin('tour_operator', `${data.name.trim()} (${data.company.trim()})`, [
    { label: 'Name', value: data.name },
    { label: 'Company', value: data.company },
    { label: 'WhatsApp', value: data.phone },
    { label: 'Email', value: data.email?.trim() || 'Not provided' },
    { label: 'Niche', value: nicheLabels(data.niches, nicheOther ?? undefined).join(', ') },
    { label: 'Source', value: context.source || 'Direct' },
  ]);
}
